import React, { useState, useEffect } from 'react';
import {
  HiOutlineXMark,
  HiOutlineTableCells,
  HiOutlineGlobeAlt,
  HiOutlineChatBubbleLeftRight,
  HiOutlineCommandLine,
} from 'react-icons/hi2';
// Same icon families the Chrome extension's automation types + ConnectClaude use
// for the real tools, so the Claude section's tool chips read as familiar.
import { FaShopify } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { IoLayers } from 'react-icons/io5';
// Claude's official mark, shown unmodified (trademark: nominative use only —
// Claude is always the counterparty, never our branding).
import ClaudeMark from './ClaudeMark';

const AVATAR_BASE = 'https://ultimatewebscraper.com/assets/images/avatars';
const AVATARS = [
  `${AVATAR_BASE}/avatar5.jpg`,
  `${AVATAR_BASE}/avatar2.jpg`,
  `${AVATAR_BASE}/avatar1.jpg`,
  `${AVATAR_BASE}/avatar4.jpg`,
  `${AVATAR_BASE}/avatar3.jpg`,
];

// ─── Inline keyframes for illustrations ──────────────────────────
const upsellStyles = `
@keyframes upsellCloudFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
@keyframes upsellPulseRing {
  0% { transform: scale(0.85); opacity: 0.5; }
  100% { transform: scale(1.6); opacity: 0; }
}
@keyframes upsellFlowDot {
  0% { left: 0%; opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { left: calc(100% - 6px); opacity: 0; }
}
@keyframes upsellCursorMove {
  0%, 100% { transform: translate(16px, -10px); opacity: 0.4; }
  30%, 70% { transform: translate(0, 0); opacity: 1; }
  45%, 55% { transform: translate(1px, 1px); opacity: 1; }
}
@keyframes upsellClickRipple {
  0%, 35% { transform: scale(0); opacity: 0; }
  42% { transform: scale(0.7); opacity: 0.4; }
  55% { transform: scale(1.4); opacity: 0; }
  100% { transform: scale(0); opacity: 0; }
}
@keyframes upsellFadeSlideIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes upsellDataPulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}
@keyframes upsellCellHighlight {
  0%, 100% { box-shadow: 0 0 0 1.5px rgba(59, 130, 246, 0.5); }
  50% { box-shadow: 0 0 0 1.5px rgba(59, 130, 246, 0.9), 0 0 6px rgba(59, 130, 246, 0.2); }
}
@keyframes upsellTerminalCursor {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
@keyframes upsellRowAppear {
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes upsellDownloadFill {
  0% { width: 0%; }
  70% { width: 100%; }
  100% { width: 100%; }
}
@keyframes upsellWebhookPulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
}
@keyframes upsellAvatarPop {
  0% { transform: scale(0); opacity: 0; }
  60% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes upsellActivitySlide {
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes upsellProgress1 {
  0% { width: 0%; }
  60% { width: 85%; }
  100% { width: 85%; }
}
@keyframes upsellProgress2 {
  0%, 15% { width: 0%; }
  75% { width: 62%; }
  100% { width: 62%; }
}
@keyframes upsellProgress3 {
  0%, 30% { width: 0%; }
  90% { width: 40%; }
  100% { width: 40%; }
}
@keyframes upsellCountUp1 {
  0% { opacity: 0; }
  60% { opacity: 1; }
}
@keyframes upsellCountUp2 {
  0%, 15% { opacity: 0; }
  75% { opacity: 1; }
}
@keyframes upsellScheduleBlink {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
@keyframes upsellClockTick {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes upsellChipResolve {
  0%, 20% { opacity: 0; transform: scale(0.6); }
  35% { opacity: 1; transform: scale(1.15); }
  45%, 100% { opacity: 1; transform: scale(1); }
}
`;

if (typeof document !== 'undefined') {
  const styleId = 'upsell-card-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = upsellStyles;
    document.head.appendChild(style);
  }
}

// ─── Illustration: Mini Extension Card ───────────────────────────
const MiniExtensionCard = () => (
  <div className="relative w-full max-w-[200px] mx-auto">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/70 overflow-hidden shadow-lg">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-red-400/60" />
          <div className="w-2 h-2 rounded-full bg-yellow-400/60" />
          <div className="w-2 h-2 rounded-full bg-green-400/60" />
        </div>
        <div className="flex-1 ml-1 h-4 rounded-md bg-gray-100 dark:bg-gray-700/50 flex items-center px-2">
          <span className="text-[7px] text-gray-400 dark:text-gray-500 font-medium tracking-wide">example.com/products</span>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="h-2 w-4/5 rounded-full bg-gray-200 dark:bg-gray-700/60" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="h-2 w-3/5 rounded-full bg-gray-200 dark:bg-gray-700/60" />
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100 dark:border-gray-700/40 bg-gray-50/50 dark:bg-gray-800/30">
        <div className="flex-1 h-6 rounded-md bg-gray-200/70 dark:bg-gray-700/40" />
        <div className="relative">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 via-cyan-500 to-purple-500 flex items-center justify-center shadow-sm shadow-cyan-500/25">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
            </svg>
          </div>
          <div
            className="absolute -inset-1 rounded-lg border border-cyan-400/30"
            style={{ animation: 'upsellClickRipple 3.5s ease-in-out infinite' }}
          />
        </div>
      </div>
    </div>
    <div
      className="absolute bottom-2 right-4 pointer-events-none"
      style={{ animation: 'upsellCursorMove 3.5s ease-in-out infinite' }}
    >
      <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
        <path d="M1 1L1 14L4.5 10.5L7.5 17L10 16L7 9L12 8.5L1 1Z" className="fill-white dark:fill-gray-200" stroke="black" strokeWidth="1.2" strokeLinejoin="round" />
      </svg>
    </div>
  </div>
);

// ─── Illustration: Cloud Processing ──────────────────────────────
const CloudProcessingCard = () => (
  <div className="relative w-44 h-44 mx-auto flex items-center justify-center">
    <div
      className="absolute inset-2 rounded-full border border-cyan-400/20 dark:border-cyan-400/10"
      style={{ animation: 'upsellPulseRing 3s ease-out infinite' }}
    />
    <div
      className="absolute inset-2 rounded-full border border-purple-400/15"
      style={{ animation: 'upsellPulseRing 3s ease-out 1s infinite' }}
    />
    <div
      className="absolute inset-2 rounded-full border border-blue-400/15"
      style={{ animation: 'upsellPulseRing 3s ease-out 2s infinite' }}
    />
    <div
      className="relative"
      style={{ animation: 'upsellCloudFloat 4s ease-in-out infinite' }}
    >
      <svg className="w-28 h-28" viewBox="-1 0 26 24" fill="none">
        <defs>
          <linearGradient id="upsellCloudGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
        <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="url(#upsellCloudGrad)" opacity="0.15" />
        <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="none" stroke="url(#upsellCloudGrad)" strokeWidth="0.6" opacity="0.7" />
      </svg>
      <img src="/logo-256.png" alt="Panda Extract" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 drop-shadow-sm" />
    </div>
  </div>
);

// ─── Illustration: Dashboard with Live Data ──────────────────────
const DashboardPreviewCard = () => (
  <div className="w-full max-w-[220px] mx-auto">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/70 overflow-hidden shadow-lg">
      <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 dark:border-gray-700/30 bg-gray-50/80 dark:bg-gray-800/40">
        <div className="h-1.5 w-14 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="h-1.5 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="ml-auto h-1.5 w-8 rounded-full bg-gray-300 dark:bg-gray-600" />
      </div>
      <div className="divide-y divide-gray-50 dark:divide-gray-700/20">
        <div className="flex items-center gap-2.5 px-3 py-2.5" style={{ animation: 'upsellFadeSlideIn 0.5s ease-out both' }}>
          <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
          <div className="flex-1 min-w-0"><div className="h-2 w-3/4 rounded-full bg-gray-200 dark:bg-gray-700/60" /></div>
          <span className="text-[8px] font-bold text-emerald-500 tabular-nums flex-shrink-0">2,847</span>
        </div>
        <div className="flex items-center gap-2.5 px-3 py-2.5" style={{ animation: 'upsellFadeSlideIn 0.5s ease-out 0.12s both' }}>
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
          <div className="flex-1 min-w-0"><div className="h-2 w-2/3 rounded-full bg-gray-200 dark:bg-gray-700/60" /></div>
          <span className="text-[8px] font-bold text-amber-500 flex-shrink-0">Running</span>
        </div>
        <div className="flex items-center gap-2.5 px-3 py-2.5" style={{ animation: 'upsellFadeSlideIn 0.5s ease-out 0.24s both' }}>
          <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
          <div className="flex-1 min-w-0"><div className="h-2 w-1/2 rounded-full bg-gray-200 dark:bg-gray-700/60" /></div>
          <span className="text-[8px] font-bold text-emerald-500 tabular-nums flex-shrink-0">12.4k</span>
        </div>
        <div className="flex items-center gap-2.5 px-3 py-2.5 opacity-30" style={{ animation: 'upsellFadeSlideIn 0.5s ease-out 0.36s both' }}>
          <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
          <div className="flex-1 min-w-0"><div className="h-2 w-2/5 rounded-full bg-gray-200 dark:bg-gray-700" /></div>
          <span className="text-[8px] font-medium text-blue-400 flex-shrink-0">Scheduled</span>
        </div>
      </div>
    </div>
  </div>
);

// ─── Flow Arrow ──────────────────────────────────────────────────
const FlowArrow = () => (
  <div className="hidden md:flex items-center justify-center w-12 flex-shrink-0 self-center">
    <div className="relative w-full h-[1.5px]">
      <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-full" />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-sm shadow-cyan-400/50"
        style={{ animation: 'upsellFlowDot 2.5s ease-in-out infinite' }}
      />
      <div className="absolute -right-0.5 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent border-l-[5px] border-l-gray-300 dark:border-l-gray-600" />
    </div>
  </div>
);

// ─── Star Rating ─────────────────────────────────────────────────
const StarRating = ({ size = 'sm' }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <svg key={i} className={size === 'lg' ? 'w-5 h-5 text-yellow-400' : 'w-3.5 h-3.5 text-yellow-400'} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

// ─── Illustration: Cloud Browsers + Scheduling ──────────────────
const CloudBrowsersIllustration = () => (
  <div className="relative w-full max-w-md mx-auto">
    {/* Ambient glow behind the stack */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/[0.07] dark:bg-cyan-500/[0.10] blur-3xl rounded-full pointer-events-none" />

    <div className="relative">
      {/* ── Browser Stack (3 overlapping windows) ── */}
      <div className="relative ml-0 sm:ml-4" style={{ height: 240 }}>

        {/* Browser 3 (back) */}
        <div className="absolute top-0 left-8 sm:left-12 w-[75%] rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-md opacity-60">
          <div className="flex items-center gap-1 px-2.5 py-1.5 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
            <div className="flex gap-0.5"><div className="w-1.5 h-1.5 rounded-full bg-red-400/50" /><div className="w-1.5 h-1.5 rounded-full bg-yellow-400/50" /><div className="w-1.5 h-1.5 rounded-full bg-green-400/50" /></div>
            <div className="flex-1 ml-1 h-3 rounded bg-gray-100 dark:bg-gray-700/50 flex items-center px-1.5">
              <span className="text-[5px] text-gray-400 dark:text-gray-500 font-medium">realestate.com/listings</span>
            </div>
          </div>
          <div className="px-3 py-2.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-[7px] text-gray-500 dark:text-gray-400 font-medium">Completed</span>
              </div>
              <span className="text-[8px] font-bold text-emerald-500">1,247</span>
            </div>
            <div className="w-full h-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-400" style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        {/* Browser 2 (middle) */}
        <div className="absolute top-10 left-4 sm:left-6 w-[80%] rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-lg opacity-80">
          <div className="flex items-center gap-1 px-2.5 py-1.5 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
            <div className="flex gap-0.5"><div className="w-1.5 h-1.5 rounded-full bg-red-400/50" /><div className="w-1.5 h-1.5 rounded-full bg-yellow-400/50" /><div className="w-1.5 h-1.5 rounded-full bg-green-400/50" /></div>
            <div className="flex-1 ml-1 h-3 rounded bg-gray-100 dark:bg-gray-700/50 flex items-center px-1.5">
              <span className="text-[5px] text-gray-400 dark:text-gray-500 font-medium">amazon.com/products</span>
            </div>
          </div>
          <div className="px-3 py-2.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" style={{ animation: 'upsellScheduleBlink 2s ease-in-out infinite' }} />
                <span className="text-[7px] text-gray-500 dark:text-gray-400 font-medium">Extracting…</span>
              </div>
              <span className="text-[8px] font-bold text-cyan-500" style={{ animation: 'upsellCountUp2 3s ease-out both' }}>489</span>
            </div>
            <div className="w-full h-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-400" style={{ animation: 'upsellProgress2 4s ease-out infinite' }} />
            </div>
          </div>
        </div>

        {/* Browser 1 (front — main) */}
        <div className="absolute top-20 left-0 w-[85%] rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-xl">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
            <div className="flex gap-1"><div className="w-2 h-2 rounded-full bg-red-400/60" /><div className="w-2 h-2 rounded-full bg-yellow-400/60" /><div className="w-2 h-2 rounded-full bg-green-400/60" /></div>
            <div className="flex-1 ml-1 h-4 rounded-md bg-gray-100 dark:bg-gray-700/50 flex items-center px-2">
              <span className="text-[7px] text-gray-400 dark:text-gray-500 font-medium">acme.com/team</span>
            </div>
          </div>
          <div className="px-3 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-[8px] text-gray-600 dark:text-gray-300 font-semibold">Extracting page 24 of 120…</span>
              </div>
              <span className="text-[9px] font-bold text-amber-500" style={{ animation: 'upsellCountUp1 3s ease-out both' }}>847</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 shadow-sm shadow-cyan-400/30" style={{ animation: 'upsellProgress1 5s ease-out infinite' }} />
            </div>
            {/* Mini data rows appearing */}
            <div className="mt-1 space-y-1">
              {['John Smith · Product Manager · San Francisco', 'Maria Chen · Engineering Lead · New York', 'Alex Kumar · Data Scientist · London'].map((row, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-gray-50 dark:bg-gray-800/50"
                  style={{ animation: `upsellFadeSlideIn 0.4s ease-out ${i * 0.15}s both` }}
                >
                  <div className="w-3 h-3 rounded-full bg-gradient-to-br from-blue-400/20 to-cyan-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[5px] font-bold text-blue-500">{row[0]}</span>
                  </div>
                  <span className="text-[6px] text-gray-500 dark:text-gray-400 truncate">{row}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 24/7 floating badge */}
        <div
          className="absolute -top-2 right-0 sm:right-4"
          style={{ animation: 'upsellCloudFloat 3s ease-in-out infinite' }}
        >
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 shadow-lg shadow-cyan-500/30">
            <svg className="w-3 h-3 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" strokeWidth="2" />
              <path strokeLinecap="round" strokeWidth="2" d="M12 7v5l3 3" />
            </svg>
            <span className="text-[10px] font-black text-white tracking-wide">24/7</span>
          </div>
        </div>

        {/* Parallel browsers badge */}
        <div className="absolute bottom-0 right-0 sm:right-4">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 shadow-md">
            <div className="flex -space-x-1">
              <div className="w-3 h-3 rounded bg-blue-400/80 border border-white dark:border-gray-800" />
              <div className="w-3 h-3 rounded bg-cyan-400/80 border border-white dark:border-gray-800" />
              <div className="w-3 h-3 rounded bg-purple-400/80 border border-white dark:border-gray-800" />
            </div>
            <span className="text-[8px] font-bold text-gray-600 dark:text-gray-300">3 browsers</span>
          </div>
        </div>
      </div>

      {/* ── Schedule Card (overlapping bottom-right) ── */}
      <div className="absolute -bottom-6 -right-2 sm:right-0 w-48 rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/90 shadow-xl overflow-hidden z-10">
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
          <div className="flex items-center gap-1.5">
            <svg className="w-3 h-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-[8px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Schedule</span>
          </div>
        </div>
        <div className="px-3 py-2.5 space-y-2">
          {/* Mini week row */}
          <div className="flex gap-1">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => {
              const active = [0, 2, 4].includes(i);
              return (
                <div
                  key={i}
                  className={`flex-1 flex items-center justify-center h-5 rounded text-[7px] font-bold ${
                    active
                      ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20 text-cyan-600 dark:text-cyan-400 border border-cyan-400/30'
                      : 'bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {day}
                </div>
              );
            })}
          </div>
          {/* Schedule rows */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full bg-cyan-400" style={{ animation: 'upsellScheduleBlink 2s ease-in-out infinite' }} />
              <span className="text-[7px] text-gray-500 dark:text-gray-400">Every Mon, Wed, Fri</span>
            </div>
            <div className="flex items-center gap-1.5">
              <svg className="w-2.5 h-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" strokeWidth="2" />
                <path strokeLinecap="round" strokeWidth="2" d="M12 7v5l3 3" />
              </svg>
              <span className="text-[7px] font-semibold text-gray-600 dark:text-gray-300">9:00 AM</span>
              <span className="text-[6px] text-gray-400 dark:text-gray-500">· auto-repeat</span>
            </div>
          </div>
          {/* Next run indicator */}
          <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[7px] font-semibold text-emerald-600 dark:text-emerald-400">Next run in 4h 23m</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ─── Illustration: Integrations Data Flow ────────────────────────
const SHEETS_DATA = [
  { name: 'MacBook Pro', price: '$1,299', stock: '142', rating: '4.8' },
  { name: 'Galaxy S24', price: '$799', stock: '89', rating: '4.6' },
  { name: 'iPad Air', price: '$599', stock: '231', rating: '4.9' },
  { name: 'Pixel 8 Pro', price: '$899', stock: '67', rating: '4.7' },
];

const IntegrationsIllustration = () => (
  <div className="relative w-full max-w-lg mx-auto">
    {/* Ambient glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-purple-500/[0.06] dark:bg-purple-500/[0.09] blur-3xl rounded-full pointer-events-none" />

    <div className="relative flex flex-col items-center gap-4" style={{ minHeight: 360 }}>

      {/* ── Source: Extraction Complete Card ── */}
      <div className="relative w-72 rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-lg z-10">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
          <div className="w-5 h-5 rounded-md bg-gradient-to-br from-blue-500 via-cyan-500 to-purple-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-[8px] font-bold text-gray-700 dark:text-gray-300">Extraction Complete</span>
          <span className="ml-auto text-[8px] font-bold text-emerald-500">2,847 rows</span>
        </div>
        <div className="px-3 py-2 space-y-1">
          <div className="flex gap-2 pb-1 border-b border-gray-100 dark:border-gray-700/30">
            <span className="text-[6px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider w-16">Name</span>
            <span className="text-[6px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider w-12">Price</span>
            <span className="text-[6px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-1">URL</span>
          </div>
          {[
            { name: 'Product A', price: '$29.99', url: 'shop.com/a' },
            { name: 'Product B', price: '$49.00', url: 'shop.com/b' },
            { name: 'Product C', price: '$15.50', url: 'shop.com/c' },
          ].map((row, i) => (
            <div key={i} className="flex gap-2 items-center" style={{ animation: `upsellFadeSlideIn 0.3s ease-out ${i * 0.1}s both` }}>
              <span className="text-[7px] text-gray-600 dark:text-gray-300 w-16 truncate">{row.name}</span>
              <span className="text-[7px] font-semibold text-gray-700 dark:text-gray-200 w-12">{row.price}</span>
              <span className="text-[6px] text-gray-400 dark:text-gray-500 flex-1 truncate">{row.url}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Flow lines branching out ── */}
      <div className="relative w-72 h-7 flex items-center justify-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1.5px] h-3 bg-gray-200 dark:bg-gray-700" />
        <div className="absolute top-3 left-[15%] right-[15%] h-[1.5px] bg-gray-200 dark:bg-gray-700" />
        <div className="absolute top-3 left-[15%] w-[1.5px] h-3 bg-gray-200 dark:bg-gray-700" />
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[1.5px] h-3 bg-gray-200 dark:bg-gray-700" />
        <div className="absolute top-3 right-[15%] w-[1.5px] h-3 bg-gray-200 dark:bg-gray-700" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-purple-400 shadow-sm shadow-purple-400/50" style={{ animation: 'upsellScheduleBlink 1.5s ease-in-out infinite' }} />
        <div className="absolute bottom-0 left-[15%] w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/50" style={{ animation: 'upsellScheduleBlink 1.5s ease-in-out 0.3s infinite' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-blue-400 shadow-sm shadow-blue-400/50" style={{ animation: 'upsellScheduleBlink 1.5s ease-in-out 0.6s infinite' }} />
        <div className="absolute bottom-0 right-[15%] w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-sm shadow-cyan-400/50" style={{ animation: 'upsellScheduleBlink 1.5s ease-in-out 0.9s infinite' }} />
      </div>

      {/* ── Destination Cards ── */}
      <div className="flex gap-4 w-full">

        {/* ━━ Google Sheets Card ━━ */}
        <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-md">
          {/* Green title bar */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-500">
            <svg className="w-3 h-3 text-white/90 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 11V9h-6V3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2v-2h-6v-6h6zM14 3l5 5h-5V3z" opacity="0.3" />
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
            </svg>
            <span className="text-[7px] font-bold text-white truncate">Products_Extract</span>
          </div>
          {/* Tab bar */}
          <div className="flex items-end px-1.5 pt-1 bg-emerald-50 dark:bg-emerald-500/10 border-b border-gray-100 dark:border-gray-700/40">
            <div className="px-2 py-0.5 bg-white dark:bg-gray-900/80 rounded-t border border-b-0 border-gray-200 dark:border-gray-700/50">
              <span className="text-[5px] font-semibold text-gray-600 dark:text-gray-400">Sheet1</span>
            </div>
            <div className="px-2 py-0.5">
              <span className="text-[5px] text-gray-400 dark:text-gray-500">Sheet2</span>
            </div>
          </div>
          {/* Spreadsheet content */}
          <div className="px-1 py-1">
            {/* Column headers */}
            <div className="flex border-b border-gray-100 dark:border-gray-700/30">
              <div className="w-4 flex-shrink-0 flex items-center justify-center py-0.5 bg-gray-50 dark:bg-gray-800/40 border-r border-gray-100 dark:border-gray-700/30">
                <span className="text-[4px] text-gray-300 dark:text-gray-600">&nbsp;</span>
              </div>
              {['A', 'B', 'C', 'D'].map((col) => (
                <div key={col} className="flex-1 flex items-center justify-center py-0.5 bg-gray-50 dark:bg-gray-800/40 border-r border-gray-100 dark:border-gray-700/30 last:border-r-0">
                  <span className="text-[4px] font-bold text-gray-400 dark:text-gray-500">{col}</span>
                </div>
              ))}
            </div>
            {/* Header row */}
            <div className="flex border-b border-gray-100 dark:border-gray-700/30 bg-emerald-50/50 dark:bg-emerald-500/5">
              <div className="w-4 flex-shrink-0 flex items-center justify-center py-[3px] bg-gray-50 dark:bg-gray-800/40 border-r border-gray-100 dark:border-gray-700/30">
                <span className="text-[4px] text-gray-400 dark:text-gray-500">1</span>
              </div>
              {['Name', 'Price', 'Stock', 'Rating'].map((h) => (
                <div key={h} className="flex-1 flex items-center px-0.5 py-[3px] border-r border-gray-100 dark:border-gray-700/30 last:border-r-0">
                  <span className="text-[5px] font-bold text-gray-700 dark:text-gray-300 truncate">{h}</span>
                </div>
              ))}
            </div>
            {/* Data rows */}
            {SHEETS_DATA.map((row, i) => (
              <div
                key={i}
                className="flex border-b border-gray-50 dark:border-gray-800/30 last:border-b-0"
                style={{ animation: `upsellRowAppear 0.4s ease-out ${0.3 + i * 0.15}s both` }}
              >
                <div className="w-4 flex-shrink-0 flex items-center justify-center py-[3px] bg-gray-50 dark:bg-gray-800/40 border-r border-gray-100 dark:border-gray-700/30">
                  <span className="text-[4px] text-gray-400 dark:text-gray-500">{i + 2}</span>
                </div>
                <div className="flex-1 flex items-center px-0.5 py-[3px] border-r border-gray-100 dark:border-gray-700/30">
                  <span className="text-[5px] text-gray-600 dark:text-gray-300 truncate">{row.name}</span>
                </div>
                <div className="flex-1 flex items-center px-0.5 py-[3px] border-r border-gray-100 dark:border-gray-700/30">
                  <span className="text-[5px] font-semibold text-gray-700 dark:text-gray-200 truncate">{row.price}</span>
                </div>
                <div className="flex-1 flex items-center px-0.5 py-[3px] border-r border-gray-100 dark:border-gray-700/30">
                  <span className="text-[5px] text-gray-500 dark:text-gray-400">{row.stock}</span>
                </div>
                <div
                  className={`flex-1 flex items-center px-0.5 py-[3px] ${i === 2 ? '' : ''}`}
                  style={i === 2 ? { animation: 'upsellCellHighlight 2s ease-in-out infinite' } : undefined}
                >
                  <span className="text-[5px] text-gray-500 dark:text-gray-400">{row.rating}</span>
                </div>
              </div>
            ))}
          </div>
          {/* Bottom status */}
          <div className="flex items-center gap-1 px-2 py-1 border-t border-gray-100 dark:border-gray-700/30 bg-gray-50/50 dark:bg-gray-800/30">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" style={{ animation: 'upsellWebhookPulse 2s ease-in-out infinite' }} />
            <span className="text-[5px] text-emerald-600 dark:text-emerald-400 font-semibold">Live sync · 2,847 rows</span>
          </div>
        </div>

        {/* ━━ Webhook Card ━━ */}
        <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-md">
          {/* Terminal title bar */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-800 dark:bg-gray-950">
            <div className="flex gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-red-400/80" />
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/80" />
              <div className="w-1.5 h-1.5 rounded-full bg-green-400/80" />
            </div>
            <span className="text-[6px] font-mono text-gray-400 ml-1">webhook-listener</span>
            <div className="ml-auto flex items-center gap-1">
              <div className="w-1 h-1 rounded-full bg-emerald-400" style={{ animation: 'upsellWebhookPulse 1.5s ease-in-out infinite' }} />
              <span className="text-[5px] text-emerald-400 font-mono">connected</span>
            </div>
          </div>
          {/* Terminal content */}
          <div className="bg-gray-900 dark:bg-gray-950 px-2 py-1.5 font-mono space-y-[3px]">
            {/* Request line */}
            <div style={{ animation: 'upsellRowAppear 0.3s ease-out 0.1s both' }}>
              <span className="text-[5px] text-emerald-400 font-bold">POST</span>
              <span className="text-[5px] text-gray-500"> https://api.yourapp.com</span>
            </div>
            {/* Headers */}
            <div style={{ animation: 'upsellRowAppear 0.3s ease-out 0.2s both' }}>
              <span className="text-[5px] text-gray-600">Content-Type: </span>
              <span className="text-[5px] text-blue-400">application/json</span>
            </div>
            {/* Separator */}
            <div className="border-t border-gray-700/50 my-0.5" style={{ animation: 'upsellFadeSlideIn 0.3s ease-out 0.3s both' }} />
            {/* JSON body */}
            <div style={{ animation: 'upsellRowAppear 0.3s ease-out 0.35s both' }}>
              <span className="text-[5px] text-gray-500">{'{'}</span>
            </div>
            <div style={{ animation: 'upsellRowAppear 0.3s ease-out 0.4s both' }}>
              <span className="text-[5px] text-cyan-400">&nbsp;&nbsp;&quot;event&quot;</span>
              <span className="text-[5px] text-gray-500">: </span>
              <span className="text-[5px] text-amber-300">&quot;extraction.done&quot;</span>
              <span className="text-[5px] text-gray-500">,</span>
            </div>
            <div style={{ animation: 'upsellRowAppear 0.3s ease-out 0.45s both' }}>
              <span className="text-[5px] text-cyan-400">&nbsp;&nbsp;&quot;rows&quot;</span>
              <span className="text-[5px] text-gray-500">: </span>
              <span className="text-[5px] text-purple-400">2847</span>
              <span className="text-[5px] text-gray-500">,</span>
            </div>
            <div style={{ animation: 'upsellRowAppear 0.3s ease-out 0.5s both' }}>
              <span className="text-[5px] text-cyan-400">&nbsp;&nbsp;&quot;data&quot;</span>
              <span className="text-[5px] text-gray-500">: [</span>
              <span className="text-[5px] text-gray-600">{'{'}&quot;name&quot;:&quot;Mac...&quot;{'}'}</span>
              <span className="text-[5px] text-gray-500">]</span>
            </div>
            <div style={{ animation: 'upsellRowAppear 0.3s ease-out 0.55s both' }}>
              <span className="text-[5px] text-gray-500">{'}'}</span>
              <span className="text-[5px] text-gray-700 inline-block w-[3px] h-[7px] ml-0.5 bg-gray-400" style={{ animation: 'upsellTerminalCursor 1s step-end infinite' }} />
            </div>
          </div>
          {/* Response */}
          <div className="bg-gray-900 dark:bg-gray-950 px-2 pb-1.5 pt-0.5 font-mono border-t border-gray-700/30">
            <div className="flex items-center gap-1.5" style={{ animation: 'upsellFadeSlideIn 0.3s ease-out 0.7s both' }}>
              <span className="text-[5px] text-gray-600">→</span>
              <div className="flex items-center gap-0.5 px-1 py-px rounded-sm bg-emerald-500/20">
                <span className="text-[6px] font-bold text-emerald-400">200</span>
                <span className="text-[5px] text-emerald-500">OK</span>
              </div>
              <span className="text-[5px] text-gray-600">12ms</span>
            </div>
          </div>
          {/* Bottom status */}
          <div className="flex items-center justify-between px-2 py-1 border-t border-gray-100 dark:border-gray-700/30 bg-gray-50/50 dark:bg-gray-800/30">
            <div className="flex items-center gap-1">
              <svg className="w-2 h-2 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-[5px] text-gray-500 dark:text-gray-400 font-medium">847 deliveries</span>
            </div>
            <span className="text-[5px] text-emerald-500 font-semibold">100%</span>
          </div>
        </div>

        {/* ━━ Export Card ━━ */}
        <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-md">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-gray-100 dark:border-gray-700/40 bg-cyan-50 dark:bg-cyan-500/10">
            <svg className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span className="text-[7px] font-bold text-cyan-700 dark:text-cyan-400">Export</span>
          </div>
          <div className="px-2 py-1.5 space-y-1.5">
            {/* CSV file - completed */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-500/20 bg-blue-50/50 dark:bg-blue-500/5 p-1.5" style={{ animation: 'upsellFadeSlideIn 0.3s ease-out 0.2s both' }}>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-6 rounded bg-blue-500/15 dark:bg-blue-500/20 flex items-center justify-center">
                  <span className="text-[5px] font-black text-blue-500">CSV</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[5px] font-semibold text-gray-700 dark:text-gray-300 truncate">products_2847.csv</p>
                  <p className="text-[4px] text-gray-400 dark:text-gray-500">1.2 MB</p>
                </div>
                <svg className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            {/* JSON file - downloading */}
            <div className="rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5 p-1.5" style={{ animation: 'upsellFadeSlideIn 0.3s ease-out 0.35s both' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-5 h-6 rounded bg-amber-500/15 dark:bg-amber-500/20 flex items-center justify-center">
                  <span className="text-[5px] font-black text-amber-500">JSON</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[5px] font-semibold text-gray-700 dark:text-gray-300 truncate">products_2847.json</p>
                  <p className="text-[4px] text-gray-400 dark:text-gray-500">2.8 MB</p>
                </div>
              </div>
              <div className="h-1 rounded-full bg-amber-200/50 dark:bg-amber-500/10 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500" style={{ animation: 'upsellDownloadFill 3s ease-out 0.5s both' }} />
              </div>
            </div>
            {/* XLSX file - queued */}
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-500/5 p-1.5" style={{ animation: 'upsellFadeSlideIn 0.3s ease-out 0.5s both' }}>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-6 rounded bg-emerald-500/15 dark:bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-[5px] font-black text-emerald-500">XLSX</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[5px] font-semibold text-gray-700 dark:text-gray-300 truncate">products_2847.xlsx</p>
                  <p className="text-[4px] text-gray-400 dark:text-gray-500">3.1 MB</p>
                </div>
                <div className="w-2.5 h-2.5 rounded-full border border-gray-300 dark:border-gray-600 border-t-cyan-500 flex-shrink-0" style={{ animation: 'upsellClockTick 1s linear infinite' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* "Auto-delivery" floating badge */}
      <div
        className="absolute -bottom-1 right-2 sm:right-6"
        style={{ animation: 'upsellCloudFloat 3.5s ease-in-out 0.5s infinite' }}
      >
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg shadow-purple-500/25">
          <svg className="w-3 h-3 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-[10px] font-black text-white tracking-wide">Auto-deliver</span>
        </div>
      </div>
    </div>
  </div>
);

// ─── Illustration: Team Access ────────────────────────────────────
const TEAM_MEMBERS = [
  { name: 'Anjin K.', avatar: `${AVATAR_BASE}/avatar1.jpg` },
  { name: 'Mike R.', avatar: `${AVATAR_BASE}/avatar2.jpg` },
  { name: 'Jess T.', avatar: `${AVATAR_BASE}/avatar3.jpg` },
];

const TeamAccessIllustration = () => (
  <div className="relative w-full max-w-lg mx-auto">
    {/* Ambient glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-indigo-500/[0.06] dark:bg-indigo-500/[0.09] blur-3xl rounded-full pointer-events-none" />

    <div className="relative flex flex-col gap-4" style={{ minHeight: 300 }}>

      {/* ── Team Members with PRO badges ── */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-lg">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
          <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="text-[9px] font-bold text-gray-700 dark:text-gray-300">Team Workspace</span>
          <div className="ml-auto text-[7px] font-semibold text-gray-400 dark:text-gray-500">3 / 10 seats</div>
        </div>

        {/* Team member avatars row */}
        <div className="flex items-center justify-center gap-5 px-4 py-3.5">
          {TEAM_MEMBERS.map((member, i) => (
            <div
              key={member.name}
              className="flex flex-col items-center gap-1.5"
              style={{ animation: `upsellAvatarPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 0.12}s both` }}
            >
              <img
                src={member.avatar}
                alt={member.name}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white dark:ring-gray-900 shadow-md"
              />
              <div className="text-center">
                <p className="text-[7px] font-bold text-gray-700 dark:text-gray-300 leading-tight">{member.name}</p>
                <span className="inline-flex items-center mt-0.5 px-1.5 py-0.5 text-[6px] font-bold uppercase tracking-wider rounded bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white leading-none">PRO</span>
              </div>
            </div>
          ))}
          {/* Add member button */}
          <div
            className="flex flex-col items-center gap-1.5"
            style={{ animation: 'upsellAvatarPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s both' }}
          >
            <div className="w-10 h-10 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-gray-800/50">
              <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
              </svg>
            </div>
            <p className="text-[7px] font-medium text-gray-400 dark:text-gray-500 leading-tight">Invite</p>
          </div>
        </div>
      </div>

      {/* ── Shared Dashboard + Extension PRO ── */}
      <div className="flex gap-3">
        {/* Shared dashboard / automations */}
        <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-md">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
            <svg className="w-3 h-3 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            <span className="text-[8px] font-bold text-gray-700 dark:text-gray-300">Shared Dashboard</span>
          </div>
          <div className="px-2 py-1.5 space-y-1">
            {[
              { name: 'Amazon Price Monitor', status: 'Running', statusColor: 'bg-emerald-500', rows: '12,340', schedule: 'Daily' },
              { name: 'Job Scraper', status: 'Scheduled', statusColor: 'bg-blue-500', rows: '5,892', schedule: 'Weekly' },
              { name: 'Competitor Analysis', status: 'Complete', statusColor: 'bg-gray-400', rows: '3,204', schedule: 'Mon/Thu' },
            ].map((auto, i) => (
              <div
                key={auto.name}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/30"
                style={{ animation: `upsellActivitySlide 0.4s ease-out ${0.3 + i * 0.15}s both` }}
              >
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${auto.statusColor}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[6px] font-bold text-gray-700 dark:text-gray-300 truncate">{auto.name}</p>
                  <p className="text-[5px] text-gray-400 dark:text-gray-500">{auto.schedule} · {auto.rows} rows</p>
                </div>
                <span className="text-[5px] font-semibold text-gray-500 dark:text-gray-400">{auto.status}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Extension PRO card */}
        <div className="w-36 rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/80 overflow-hidden shadow-md">
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
            <svg className="w-3 h-3 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
            </svg>
            <span className="text-[8px] font-bold text-gray-700 dark:text-gray-300">Extension</span>
          </div>
          <div className="px-3 py-3 flex flex-col items-center gap-2">
            {/* Mini extension icon */}
            <div
              className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 via-cyan-500 to-purple-500 flex items-center justify-center shadow-lg shadow-cyan-500/20"
              style={{ animation: 'upsellAvatarPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both' }}
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-purple-500/10 border border-cyan-400/20 mb-1">
                <span className="text-[6px] font-black bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent uppercase tracking-wider">PRO Unlocked</span>
              </div>
              <p className="text-[6px] text-gray-400 dark:text-gray-500 leading-relaxed">All members get full PRO features</p>
            </div>
            {/* Mini feature checks */}
            <div className="w-full space-y-1">
              {['Unlimited extractions', 'Image Downloads', 'All export formats'].map((feat, i) => (
                <div
                  key={feat}
                  className="flex items-center gap-1.5"
                  style={{ animation: `upsellFadeSlideIn 0.3s ease-out ${0.6 + i * 0.1}s both` }}
                >
                  <svg className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[6px] text-gray-600 dark:text-gray-400 font-medium">{feat}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating badge */}
      <div
        className="absolute -bottom-1 left-2 sm:left-6"
        style={{ animation: 'upsellCloudFloat 3.5s ease-in-out 0.8s infinite' }}
      >
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 shadow-lg shadow-indigo-500/25">
          <svg className="w-3 h-3 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-[10px] font-black text-white tracking-wide">Shared workspace</span>
        </div>
      </div>
    </div>
  </div>
);

// ─── Illustration: Now works with Claude ─────────────────────────
// A faithful Claude.ai chat canvas (warm paper surface, coral accent), ported
// from ConnectClaude's AutoChat so it reads as the *same* Claude experience the
// product ships. Auto-cycles through real jobs: a plain-English ask resolves
// into tool chips (spinner → check) that name our ACTUAL tools, then a clean
// result table — the payoff. Non-interactive (the modal owns interaction);
// respects prefers-reduced-motion by holding on the first scenario.

// Claude's "C" glyph — a coral badge (Claude's warm accent), used nominatively
// to mark Claude's side of the chat. Copied verbatim from ConnectClaude; never
// our own branding, and never a recolor of the actual Claude mark image.
const ClaudeGlyph = ({ className = 'w-5 h-5', text = 'text-[11px]' }) => (
  <span
    className={`flex-shrink-0 flex items-center justify-center rounded-md bg-[#d97757] text-white font-bold ${text} ${className}`}
    role="img"
    aria-label="Claude"
  >
    C
  </span>
);

// Auto-playing hero conversations — each a real job Claude runs through our
// connector. Every chip names a REAL tool, so nothing here promises something
// the connector can't do (Shopify Extractor, Sitemap Explorer, Product &
// catalogue, Email Extractor). The reply resolves into a real-looking table.
const CLAUDE_SCENARIOS = [
  {
    prompt: 'Scrape the whole catalogue at shop.example.com into a table.',
    chips: [
      { Icon: HiOutlineGlobeAlt, label: 'Sitemap Explorer · 128 product URLs', color: 'text-cyan-500 dark:text-cyan-400' },
      { Icon: IoLayers, label: 'Product & catalogue · names, prices, images', color: 'text-blue-500 dark:text-blue-400' },
    ],
    reply: 'Pulled all 128 products into a clean table:',
    meta: '128 rows · 5 columns',
    table: {
      cols: ['Product', 'Price'],
      rows: [
        { img: 'watch-steel-red.webp', name: 'Steel Watch, Red Dial', cells: ['$149.00'] },
        { img: 'watch-gold-green.webp', name: 'Gold Day-Date Watch', cells: ['$129.00'] },
        { img: 'watch-resin-teal.webp', name: 'Digital Sport Watch, Teal', cells: ['$89.00'] },
      ],
    },
  },
  {
    prompt: 'Get this Shopify collection — one row per variant, with SKU & price.',
    chips: [
      { Icon: FaShopify, label: 'Shopify Extractor · Sneakers · 34 products', color: 'text-shopify-500 dark:text-shopify-400' },
    ],
    reply: '34 sneakers — one row per variant, with price & SKU:',
    meta: '34 products · 96 variants',
    table: {
      cols: ['Product', 'SKU', 'Price'],
      rows: [
        { img: 'shoe-white-low.webp', name: 'Low-Top Sneaker, White', cells: ['LOW-WHT', '$120'] },
        { img: 'shoe-red-high.webp', name: 'High-Top Sneaker, Red', cells: ['HGH-RED', '$135'] },
        { img: 'shoe-green-mid.webp', name: 'Turf Cleat, Neon Green', cells: ['TRF-GRN', '$95'] },
      ],
    },
  },
  {
    prompt: 'Find the contact emails on these 150 supplier sites.',
    chips: [
      { Icon: MdEmail, label: 'Email Extractor · scanned 150 sites', color: 'text-green-500 dark:text-green-400' },
    ],
    reply: 'Collected 132 verified emails into a table:',
    meta: '132 emails · 150 sites',
    table: {
      cols: ['Company', 'Contact email'],
      rows: [
        { mono: 'N', tone: 'indigo', name: 'Northwind Textiles', cells: ['ops@northwind.co'] },
        { mono: 'H', tone: 'teal', name: 'Harbor Supply Co.', cells: ['hello@harborsupply.com'] },
        { mono: 'C', tone: 'amber', name: 'Cedar & Co.', cells: ['sales@cedarco.io'] },
      ],
    },
  },
];

// One tool-call row inside the chat — a tinted icon tile, the tool label, then a
// status that starts as a coral spinner and resolves to an emerald check a beat
// later, so it reads as work happening (mirrors ConnectClaude's ScenarioChip).
const ClaudeToolChip = ({ Icon, label, color, delay }) => {
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDone(false);
    const t = setTimeout(() => setDone(true), (delay + 0.9) * 1000);
    return () => clearTimeout(t);
  }, [delay, label]);

  return (
    <div style={{ animation: `upsellFadeSlideIn 0.45s ease-out ${delay}s both` }}>
      <div className="inline-flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-lg bg-[#00000005] dark:bg-white/[0.04] border border-[#0000000d] dark:border-white/[0.06] max-w-full">
        <span className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center bg-white/70 dark:bg-white/[0.06] border border-[#0000000a] dark:border-white/[0.06] ${color}`}>
          <Icon className="w-3 h-3" />
        </span>
        <span className="min-w-0 truncate text-[11px] font-medium text-[#5a5952] dark:text-gray-300">{label}</span>
        {done ? (
          <span
            className="flex-shrink-0 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center"
            style={{ animation: 'upsellChipResolve 0.35s ease-out both' }}
          >
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
            </svg>
          </span>
        ) : (
          <span
            className="flex-shrink-0 w-3.5 h-3.5 rounded-full border-[1.5px] border-gray-300 dark:border-gray-600 border-t-[#d97757]"
            style={{ animation: 'upsellClockTick 0.7s linear infinite' }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
};

// A colour per company, so a column of monograms reads like a column of real
// favicons rather than three copies of one grey chip.
const MONO_TONES = {
  indigo: 'bg-indigo-50 text-indigo-500 ring-indigo-400/30 dark:bg-indigo-400/15 dark:text-indigo-300 dark:ring-indigo-400/25',
  teal: 'bg-teal-50 text-teal-600 ring-teal-400/30 dark:bg-teal-400/15 dark:text-teal-300 dark:ring-teal-400/25',
  amber: 'bg-amber-50 text-amber-600 ring-amber-400/30 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/25',
};

// A single result row in the chat's mini table — a product photo (or an initial,
// for rows that stand for a company), the name, then trailing cells (SKU, price,
// email…). Last column is the emphasised value. Tiles here are 20px, not 28px as
// on /connect-claude, so only tightly-framed product shots stay readable.
const ClaudeResultRow = ({ row, delay }) => (
  <div className="flex items-center gap-2 px-2.5 py-1.5" style={{ animation: `upsellRowAppear 0.4s ease-out ${delay}s both` }}>
    {row.img ? (
      <img
        src={`/assets/demo-products/${row.img}`}
        alt=""
        width={20}
        height={20}
        className="flex-shrink-0 w-5 h-5 rounded-md object-cover ring-1 ring-black/[0.08] dark:ring-white/[0.06]"
      />
    ) : (
      <span
        className={`flex-shrink-0 w-5 h-5 rounded-md ring-1 flex items-center justify-center text-[9px] font-semibold ${MONO_TONES[row.tone] ?? MONO_TONES.indigo}`}
        aria-hidden="true"
      >
        {row.mono}
      </span>
    )}
    <span className="flex-1 min-w-0 truncate text-[11px] font-medium text-[#3d3d3a] dark:text-gray-200">{row.name}</span>
    {row.cells.map((cell, i) => {
      const last = i === row.cells.length - 1;
      return (
        <span
          key={i}
          className={`flex-shrink-0 tabular-nums ${
            last
              ? 'text-[11px] font-semibold text-[#2d2d2a] dark:text-gray-100'
              : 'hidden sm:inline text-[9px] font-mono text-[#8a8980] dark:text-gray-500'
          }`}
        >
          {cell}
        </span>
      );
    })}
  </div>
);

const NowWorksWithClaudeIllustration = () => {
  const [index, setIndex] = useState(0);

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduceMotion) return undefined;
    const t = setTimeout(() => setIndex((i) => (i + 1) % CLAUDE_SCENARIOS.length), 5200);
    return () => clearTimeout(t);
  }, [index, reduceMotion]);

  const scenario = CLAUDE_SCENARIOS[index];
  const chipBase = 0.6;
  const replyDelay = chipBase + scenario.chips.length * 0.5 + 0.35;

  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Soft warm ambient shadow — Claude's canvas is calm; no multicolor glow */}
      <div className="absolute -inset-4 bg-[#d97757]/[0.06] dark:bg-black/40 rounded-[2rem] blur-3xl pointer-events-none" aria-hidden="true" />

      {/* The Claude.ai canvas — warm paper (light) / flat warm-dark (dark). This
          IS the chat surface, not an app window. */}
      <div
        className="relative rounded-[1.5rem] border border-[#00000010] dark:border-white/[0.06] bg-[#faf9f5] dark:bg-[#262624] shadow-xl shadow-[#00000012] dark:shadow-black/40 overflow-hidden"
        style={{ animation: 'upsellCloudFloat 7s ease-in-out infinite' }}
      >
        {/* Header — Claude's mark + name, and a pill showing OUR product
            connected to it (Claude is the surface; we're the tool it connects
            to — nominative, never a co-brand). */}
        <div className="flex items-center gap-2 px-4 pt-3.5 pb-1.5">
          <ClaudeGlyph className="w-5 h-5" />
          <span className="text-[13px] font-medium text-[#3d3d3a] dark:text-gray-200 tracking-tight">Claude</span>
          <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#00000006] dark:bg-white/[0.04] flex-shrink-0">
            <span className="w-3.5 h-3.5 rounded-[3px] overflow-hidden flex-shrink-0">
              <img src="/logo-128.png" alt="Ultimate Web Scraper" className="w-full h-full" />
            </span>
            <span className="text-[9px] font-medium text-[#73726c] dark:text-gray-400 truncate max-w-[92px]">Ultimate Web Scraper</span>
            <span className="text-[9px] font-semibold text-emerald-600 dark:text-emerald-400 flex-shrink-0 border-l border-[#0000000f] dark:border-white/[0.08] pl-1.5">connected</span>
            <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
          </span>
        </div>

        {/* Conversation — keyed by scenario so the reveal replays each cycle. */}
        <div key={index} className="px-4 py-3 space-y-3.5 min-h-[248px]">
          {/* User turn — right-aligned, in Claude's warm input bubble. */}
          <div className="flex justify-end" style={{ animation: 'upsellFadeSlideIn 0.5s ease-out 0.15s both' }}>
            <div className="max-w-[85%] px-3.5 py-2 rounded-[1.25rem] rounded-br-lg bg-[#00000008] dark:bg-white/[0.05] text-[#3d3d3a] dark:text-gray-100 text-[13px] leading-relaxed break-words">
              {scenario.prompt}
            </div>
          </div>

          {/* Assistant turn — Claude glyph, then tool steps + reply on the canvas. */}
          <div className="flex gap-2.5">
            <ClaudeGlyph className="w-5 h-5 mt-0.5" />
            <div className="flex-1 min-w-0 space-y-2.5">
              {scenario.chips.map((chip, i) => (
                <ClaudeToolChip
                  key={`${index}-${i}`}
                  Icon={chip.Icon}
                  label={chip.label}
                  color={chip.color}
                  delay={chipBase + i * 0.5}
                />
              ))}

              <div className="space-y-2" style={{ animation: `upsellFadeSlideIn 0.5s ease-out ${replyDelay}s both` }}>
                <p className="text-[13px] text-[#3d3d3a] dark:text-gray-200 leading-relaxed">{scenario.reply}</p>

                {/* Result table — a quiet card on the warm canvas */}
                <div className="rounded-lg border border-[#0000000d] dark:border-white/[0.07] bg-white/60 dark:bg-white/[0.02] overflow-hidden">
                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#00000004] dark:bg-white/[0.03] border-b border-[#0000000a] dark:border-white/[0.05]">
                    <span className="w-5 flex-shrink-0" aria-hidden="true" />
                    {scenario.table.cols.map((col, i) => {
                      const last = i === scenario.table.cols.length - 1;
                      const mid = i > 0 && !last;
                      return (
                        <span
                          key={col}
                          className={`text-[9px] font-semibold uppercase tracking-wider text-[#8a8980] dark:text-gray-500 ${
                            i === 0 ? 'flex-1' : mid ? 'hidden sm:inline flex-shrink-0' : 'flex-shrink-0'
                          }`}
                        >
                          {col}
                        </span>
                      );
                    })}
                  </div>
                  <div className="divide-y divide-[#0000000a] dark:divide-white/[0.04]">
                    {scenario.table.rows.map((row, i) => (
                      <ClaudeResultRow key={i} row={row} delay={replyDelay + 0.2 + i * 0.12} />
                    ))}
                  </div>
                  <div
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#00000004] dark:bg-white/[0.03] border-t border-[#0000000a] dark:border-white/[0.05]"
                    style={{ animation: `upsellFadeSlideIn 0.4s ease-out ${replyDelay + 0.6}s both` }}
                  >
                    <HiOutlineTableCells className="w-3 h-3 text-[#8a8980] dark:text-gray-500 flex-shrink-0" />
                    <span className="text-[10px] text-[#8a8980] dark:text-gray-500">{scenario.meta}</span>
                    <span className="ml-auto text-[10px] font-medium text-[#d97757]">View table →</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scenario dots — indicate which prompt is playing. */}
        <div className="flex items-center justify-center gap-1.5 pb-3">
          {CLAUDE_SCENARIOS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Show example ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? 'w-4 bg-[#d97757]' : 'w-1.5 bg-[#00000015] dark:bg-gray-600 hover:bg-[#00000025] dark:hover:bg-gray-500'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Floating "Works with Claude Code" badge — mirrors the other sections'
          floating badges, warm coral, with the unmodified Claude mark. */}
      <div className="absolute -bottom-4 right-2 sm:right-4" style={{ animation: 'upsellCloudFloat 3s ease-in-out 0.4s infinite' }}>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#d97757] shadow-lg shadow-[#d97757]/30">
          <ClaudeMark className="w-3.5 h-3.5" rounded="rounded-[3px]" />
          <span className="text-[10px] font-black text-white tracking-wide">Works with Claude Code</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────
const UpsellFloatingCard = ({ onClose, onUpgrade, dismissible = true, ctaLabel, children }) => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  // Hard paywall (non-dismissible) appears immediately; the soft upsell waits.
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), dismissible ? 2000 : 0);
    return () => clearTimeout(timer);
  }, [dismissible]);

  useEffect(() => {
    if (!dismissible) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [dismissible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [visible]);

  const handleDismiss = () => {
    if (!dismissible || closing) return;
    setClosing(true);
    setTimeout(() => onClose?.(), 300);
  };

  const ctaText = ctaLabel || 'View Plans';

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 transition-opacity duration-300 ${
        closing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 dark:bg-black/60 backdrop-blur-sm" onClick={dismissible ? handleDismiss : undefined} />

      {/* Modal */}
      <div
        className={`relative w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-white dark:bg-gray-950 rounded-2xl border border-gray-200/80 dark:border-gray-700/50 shadow-2xl shadow-cyan-500/10 dark:shadow-cyan-500/5 transition-all duration-300 ${
          closing ? 'scale-95 opacity-0' : 'animate-scale-up'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top gradient bar */}
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 sticky top-0 z-10" />

        {/* Close */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="absolute top-5 right-5 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors z-20"
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        )}

        {/* ════════════════════════════════════════════
            HERO
        ════════════════════════════════════════════ */}
        <div className="relative overflow-hidden">
          {/* Gradient wash */}
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-50/80 via-blue-50/30 to-transparent dark:from-cyan-500/[0.07] dark:via-blue-500/[0.04] dark:to-transparent pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.04] via-transparent to-purple-500/[0.05] dark:from-blue-500/[0.05] dark:via-transparent dark:to-purple-500/[0.06] pointer-events-none" />

          {/* Ambient glows */}
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-500/[0.08] dark:bg-blue-500/[0.10] blur-3xl rounded-full pointer-events-none" />
          <div className="absolute -top-10 right-0 w-60 h-60 bg-purple-500/[0.07] dark:bg-purple-500/[0.09] blur-3xl rounded-full pointer-events-none" />
          <div className="absolute top-16 left-1/2 -translate-x-1/2 w-80 h-40 bg-cyan-400/[0.06] dark:bg-cyan-400/[0.08] blur-3xl rounded-full pointer-events-none" />

          <div className="relative text-center px-6 sm:px-12 pt-8 sm:pt-10 pb-8">
            {/* Badge */}
            <div className="block">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-purple-500/10 border border-cyan-400/20 mb-5">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider">
                  Cloud Platform + Chrome Extension
                </span>
              </div>
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-[1.15] mb-5">
              <span className="text-gray-900 dark:text-gray-100">Unlock the </span>
              <span className="bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent">
                Cloud Platform
              </span>
            </h2>

            <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto leading-relaxed mb-8">
              The cloud platform is a PRO feature. Upgrade to run extractions in the
              cloud 24/7 — configure in the Chrome extension, get fresh data on
              autopilot, no browser needed.
            </p>

            {/* Primary CTA */}
            <button
              onClick={onUpgrade}
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-bold text-base bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 text-white shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.03] transition-all duration-200 group"
            >
              {ctaText}
              <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
            </button>

            {dismissible && (
              <button
                onClick={handleDismiss}
                className="block mx-auto mt-3 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Maybe later
              </button>
            )}

            {children}

            {/* Social proof */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 pt-8 border-t border-gray-100 dark:border-gray-800/60">
              <div className="flex -space-x-2.5">
                {AVATARS.map((src, i) => (
                  <img key={i} src={src} alt="" className="w-9 h-9 rounded-full border-2 border-white dark:border-gray-950 object-cover bg-gray-200 dark:bg-gray-700 shadow-sm" />
                ))}
              </div>
              <div className="flex items-center gap-x-4 text-sm text-gray-500 dark:text-gray-400">
                <span><span className="font-bold text-gray-800 dark:text-gray-100">80,000+</span> professionals</span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span><span className="font-bold text-gray-800 dark:text-gray-100">10M+</span> pages collected</span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span className="flex items-center gap-1">
                  <span className="font-bold text-gray-800 dark:text-gray-100">4.8</span>
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                  <span className="text-gray-500 dark:text-gray-400">Chrome Store</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            HOW IT WORKS — Animated Flow
        ════════════════════════════════════════════ */}
        <div className="px-6 sm:px-12 py-10 bg-gray-50/50 dark:bg-gray-900/50 border-y border-gray-100 dark:border-gray-800/60">
          <h3 className="text-center text-xl sm:text-2xl font-black text-gray-900 dark:text-gray-100 mb-2">
            How it works
          </h3>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-10">
            Three steps to automated data collection
          </p>

          {/* Flow visualization */}
          <div className="relative">
            {/* Background glows */}
            <div className="absolute -left-16 top-1/2 -translate-y-1/2 w-48 h-48 bg-blue-500/5 dark:bg-blue-500/8 blur-3xl rounded-full pointer-events-none" />
            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-48 h-48 bg-cyan-500/5 dark:bg-cyan-500/6 blur-3xl rounded-full pointer-events-none" />
            <div className="absolute -right-16 top-1/2 -translate-y-1/2 w-48 h-48 bg-purple-500/5 dark:bg-purple-500/8 blur-3xl rounded-full pointer-events-none" />

            <div className="relative flex flex-col md:flex-row items-center md:items-stretch gap-4 md:gap-0">

              {/* Step 1 */}
              <div className="flex-1">
                <div className="relative overflow-hidden rounded-2xl h-full p-5 lg:p-6 bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/40 shadow-lg shadow-gray-200/40 dark:shadow-black/20 hover:shadow-xl transition-all">
                  <div className="inline-flex items-center justify-center w-6 h-6 rounded-full mb-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white text-[11px] font-bold shadow-sm shadow-blue-500/20">1</div>
                  <div className="mb-4 flex items-center justify-center min-h-[160px]">
                    <MiniExtensionCard />
                  </div>
                  <h4 className="text-base font-bold text-gray-800 dark:text-gray-200 mb-1">Use Chrome extension</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">Set up your extraction in the extension — select elements, choose pagination</p>
                </div>
              </div>

              <FlowArrow />

              {/* Step 2 — Featured */}
              <div className="flex-1 md:flex-[1.15]">
                <div className="relative overflow-hidden rounded-2xl h-full p-5 lg:p-6 bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl border border-cyan-200/50 dark:border-cyan-500/15 shadow-lg shadow-cyan-500/8 dark:shadow-cyan-500/5 hover:shadow-xl transition-all">
                  <div className="inline-flex items-center justify-center w-6 h-6 rounded-full mb-4 bg-gradient-to-br from-cyan-500 to-cyan-600 text-white text-[11px] font-bold shadow-sm shadow-cyan-500/20">2</div>
                  <div className="mb-4 flex items-center justify-center min-h-[160px]">
                    <CloudProcessingCard />
                  </div>
                  <h4 className="text-base font-bold text-gray-800 dark:text-gray-200 mb-1">Runs in the Cloud</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">Click the cloud button — we handle the rest 24/7 on our servers</p>
                </div>
              </div>

              <FlowArrow />

              {/* Step 3 */}
              <div className="flex-1">
                <div className="relative overflow-hidden rounded-2xl h-full p-5 lg:p-6 bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/40 shadow-lg shadow-gray-200/40 dark:shadow-black/20 hover:shadow-xl transition-all">
                  <div className="inline-flex items-center justify-center w-6 h-6 rounded-full mb-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white text-[11px] font-bold shadow-sm shadow-purple-500/20">3</div>
                  <div className="mb-4 flex items-center justify-center min-h-[160px]">
                    <DashboardPreviewCard />
                  </div>
                  <h4 className="text-base font-bold text-gray-800 dark:text-gray-200 mb-1">View Results Here</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">Data appears automatically — schedule recurring runs, export anytime</p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            {[
              { icon: <ClaudeMark className="w-4 h-4" rounded="rounded-[3px]" />, text: 'Works with Claude' },
              { icon: '☁️', text: 'Runs 24/7' },
              { icon: '📅', text: 'Scheduling' },
              { icon: '🤖', text: 'AI Page Unblocker' },
              { icon: '🏷️', text: 'AI Data Labeling' },
              { icon: '🔗', text: 'Webhooks & Sheets' },
              { icon: '🌐', text: 'Proxies' },
              { icon: '👥', text: 'Team Access' },
              { icon: '🧩', text: 'Extension PRO included' },
            ].map((pill) => (
              <div
                key={pill.text}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-gray-800/50 border border-gray-200/60 dark:border-gray-700/30 text-sm text-gray-700 dark:text-gray-300 font-medium shadow-sm"
              >
                <span className="flex items-center">{pill.icon}</span>
                <span>{pill.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ════════════════════════════════════════════
            FEATURE DEEP-DIVE: Cloud Browsers + Scheduling
        ════════════════════════════════════════════ */}
        <div className="px-6 sm:px-12 py-12 relative overflow-hidden">
          {/* Ambient glows */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/[0.04] dark:bg-cyan-500/[0.06] blur-3xl rounded-full pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/[0.04] dark:bg-purple-500/[0.06] blur-3xl rounded-full pointer-events-none" />

          <div className="relative flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
            {/* Illustration side */}
            <div className="flex-1 w-full lg:w-auto min-w-0 order-1 lg:order-2">
              <CloudBrowsersIllustration />
            </div>

            {/* Text side */}
            <div className="flex-1 min-w-0 order-2 lg:order-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 dark:bg-cyan-500/15 border border-cyan-400/20 mb-5">
                <svg className="w-3.5 h-3.5 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
                </svg>
                <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider">Cloud Browsers</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tight leading-tight mb-4">
                Run extractions{' '}
                <span className="bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent">
                  24/7 on autopilot
                </span>
              </h3>

              <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
                Close your laptop and let the cloud do the work. Multiple browser instances run your extractions simultaneously — even while you sleep.
              </p>

              {/* Key points */}
              <div className="space-y-4">
                {[
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      </svg>
                    ),
                    title: 'Up to 5 parallel browsers',
                    desc: 'Run multiple extractions simultaneously — like having a team of scrapers working for you',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    ),
                    title: 'Schedule extractions',
                    desc: 'Set it and forget it — daily, weekly, or custom schedules keep your data always fresh',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    ),
                    title: 'No browser needed',
                    desc: 'Your extractions run on our infrastructure — close your laptop, results still arrive',
                  },
                ].map((point) => (
                  <div key={point.title} className="flex items-start gap-3 text-left">
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-purple-500/10 border border-cyan-400/10 flex items-center justify-center text-cyan-600 dark:text-cyan-400">
                      {point.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-0.5">{point.title}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            FEATURE DEEP-DIVE: Integrations
        ════════════════════════════════════════════ */}
        <div className="px-6 sm:px-12 py-12 relative overflow-hidden bg-gray-50/50 dark:bg-gray-900/50 border-y border-gray-100 dark:border-gray-800/60">
          {/* Ambient glows */}
          <div className="absolute top-0 left-0 w-72 h-72 bg-purple-500/[0.04] dark:bg-purple-500/[0.06] blur-3xl rounded-full pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-emerald-500/[0.03] dark:bg-emerald-500/[0.05] blur-3xl rounded-full pointer-events-none" />

          <div className="relative flex flex-col lg:flex-row items-center gap-10 lg:gap-12">
            {/* Illustration side — LEFT on desktop */}
            <div className="flex-1 lg:flex-[1.3] w-full lg:w-auto min-w-0 order-1">
              <IntegrationsIllustration />
            </div>

            {/* Text side — RIGHT on desktop */}
            <div className="flex-1 min-w-0 order-2 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 dark:bg-purple-500/15 border border-purple-400/20 mb-5">
                <svg className="w-3.5 h-3.5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span className="text-xs font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider">Integrations</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tight leading-tight mb-4">
                Send data{' '}
                <span className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 bg-clip-text text-transparent">
                  wherever you need it
                </span>
              </h3>

              <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
                Your extracted data flows automatically to the tools you already use. No manual exports, no copy-paste — just connected workflows.
              </p>

              {/* Key points */}
              <div className="space-y-4">
                {[
                  {
                    icon: (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                        <line x1="3" y1="15" x2="21" y2="15" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                        <line x1="10" y1="3" x2="10" y2="21" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                      </svg>
                    ),
                    title: 'Google Sheets',
                    desc: 'Data syncs directly to your spreadsheet — always up to date, ready to share with your team',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    ),
                    title: 'Webhooks',
                    desc: 'Push data to any URL in real-time — connect to Zapier, Make, n8n, or your own API',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    ),
                    title: 'Export anywhere',
                    desc: 'Download as CSV, JSON, or Excel — one click and your data is ready',
                  },
                ].map((point) => (
                  <div key={point.title} className="flex items-start gap-3 text-left">
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-rose-500/10 border border-purple-400/10 flex items-center justify-center text-purple-600 dark:text-purple-400">
                      {point.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-0.5">{point.title}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            FEATURE DEEP-DIVE: Now works with Claude
        ════════════════════════════════════════════ */}
        <div className="px-6 sm:px-12 py-12 relative overflow-hidden bg-[#faf9f5]/60 dark:bg-[#262624]/40 border-y border-[#00000010] dark:border-white/[0.06]">
          {/* Ambient glows — warm coral (Claude's calm canvas), plus a cool one
              tying back to our product */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-[#d97757]/[0.06] dark:bg-[#d97757]/[0.08] blur-3xl rounded-full pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/[0.04] dark:bg-cyan-500/[0.06] blur-3xl rounded-full pointer-events-none" />

          <div className="relative flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
            {/* Illustration side — RIGHT on desktop, first on mobile */}
            <div className="flex-1 lg:flex-[1.3] w-full lg:w-auto min-w-0 order-1 lg:order-2">
              <NowWorksWithClaudeIllustration />
            </div>

            {/* Text side — LEFT on desktop, second on mobile */}
            <div className="flex-1 min-w-0 order-2 lg:order-1 text-center lg:text-left">
              {/* Eyebrow — the real Claude mark, unmodified, stating factual
                  compatibility (nominative use; not a partnership claim) */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#d97757]/10 dark:bg-[#d97757]/15 border border-[#d97757]/25 mb-5">
                <ClaudeMark className="w-3.5 h-3.5" rounded="rounded-[3px]" />
                <span className="text-xs font-bold text-[#c15f3c] dark:text-[#e08a6a] uppercase tracking-wider">Works with Claude</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tight leading-tight mb-4">
                Connect Ultimate Web Scraper to Claude —{' '}
                <span className="bg-gradient-to-r from-[#d97757] to-[#e8a37d] bg-clip-text text-transparent">
                  then just ask
                </span>
              </h3>

              <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
                Connect your Ultimate Web Scraper account to Claude once, and it becomes a tool Claude can use —
                so you can scrape the web right from your chat and get clean, structured data back.
              </p>

              {/* Key points — every claim maps to a real capability. Copy is
                  nominative: Claude is the app you connect TO, never a co-brand
                  or partner; we never imply Anthropic endorsement. */}
              <div className="space-y-4">
                {[
                  {
                    icon: <HiOutlineChatBubbleLeftRight className="w-5 h-5" />,
                    title: 'Ask in plain English',
                    desc: 'No selectors, no code — just tell Claude what you want scraped.',
                  },
                  {
                    icon: <HiOutlineCommandLine className="w-5 h-5" />,
                    title: 'Use it in any Claude app',
                    desc: 'One connection works in the Claude app, on the web, and in Claude Code.',
                  },
                  {
                    icon: <HiOutlineTableCells className="w-5 h-5" />,
                    title: 'Get clean tables back',
                    desc: 'Your real extractors run and hand back a table you can filter and export.',
                  },
                ].map((point) => (
                  <div key={point.title} className="flex items-start gap-3 text-left">
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#d97757]/10 dark:bg-[#d97757]/15 border border-[#d97757]/15 flex items-center justify-center text-[#c15f3c] dark:text-[#e08a6a]">
                      {point.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-0.5">{point.title}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            FEATURE DEEP-DIVE: Team Access
        ════════════════════════════════════════════ */}
        <div className="px-6 sm:px-12 py-12 relative overflow-hidden">
          {/* Ambient glows */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-indigo-500/[0.04] dark:bg-indigo-500/[0.06] blur-3xl rounded-full pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/[0.03] dark:bg-blue-500/[0.05] blur-3xl rounded-full pointer-events-none" />

          <div className="relative flex flex-col lg:flex-row items-center gap-10 lg:gap-12">
            {/* Illustration side — RIGHT on desktop */}
            <div className="flex-1 lg:flex-[1.3] w-full lg:w-auto min-w-0 order-1 lg:order-2">
              <TeamAccessIllustration />
            </div>

            {/* Text side — LEFT on desktop */}
            <div className="flex-1 min-w-0 order-2 lg:order-1 text-center lg:text-left">
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 dark:bg-indigo-500/15 border border-indigo-400/20">
                  <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Team Access</span>
                </div>
              </div>

              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tight leading-tight mb-4">
                Work together with{' '}
                <span className="bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  your team
                </span>
              </h3>

              <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
                Invite your teammates to a shared workspace. Everyone gets PRO extension access and a common dashboard to manage automations and data.
              </p>

              {/* Key points */}
              <div className="space-y-4">
                {[
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    ),
                    title: 'PRO extension for every member',
                    desc: 'All team members unlock the full PRO Chrome extension — no separate licenses needed',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    ),
                    title: 'Common dashboard',
                    desc: 'One shared place to manage automations, schedules, and your cloud database',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    ),
                    title: 'Invite your team',
                    desc: 'Add teammates to your workspace — analysts, developers, managers — everyone stays in sync',
                  },
                ].map((point) => (
                  <div key={point.title} className="flex items-start gap-3 text-left">
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500/10 via-blue-500/10 to-cyan-500/10 border border-indigo-400/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      {point.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-0.5">{point.title}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            FEATURED TESTIMONIAL
        ════════════════════════════════════════════ */}
        <div className="px-6 sm:px-12 py-10">
          {/* Big testimonial */}
          <div className="max-w-2xl mx-auto text-center mb-8">
            <StarRating size="lg" />
            <blockquote className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-gray-100 leading-relaxed mt-5 mb-6">
              &ldquo;By far the best, most complete, flexible, and easiest to use scraper!{' '}
              <span className="font-black">Don&apos;t hesitate!</span>&rdquo;
            </blockquote>
            <div className="flex items-center justify-center gap-3">
              <img
                src={`${AVATAR_BASE}/avatar9.jpg`}
                alt="Ken Krause"
                className="w-11 h-11 rounded-full object-cover bg-gray-200 dark:bg-gray-700 ring-2 ring-gray-200 dark:ring-gray-700"
              />
              <div className="text-left">
                <p className="text-sm font-bold text-gray-800 dark:text-gray-200">Ken Krause</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">Business Owner</p>
              </div>
            </div>
          </div>

          {/* Secondary testimonials */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {[
              { author: 'Nitin Bansal', avatar: `${AVATAR_BASE}/avatar7.png`, quote: 'Excellent extension. Works so flawlessly well. Beautiful UI and very well done UX.' },
              { author: 'Jeff', avatar: `${AVATAR_BASE}/avatar4.jpg`, quote: 'Works like a charm, PRO is worth it! The best and only scraper I use!' },
            ].map(({ quote, author, avatar }) => (
              <div key={author} className="relative rounded-xl border border-gray-100 dark:border-gray-800/60 bg-gray-50/80 dark:bg-gray-800/30 px-5 py-4">
                <span className="absolute top-2 right-3 text-3xl font-serif text-gray-200 dark:text-gray-700/50 leading-none select-none">&ldquo;</span>
                <StarRating />
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed italic">&ldquo;{quote}&rdquo;</p>
                <div className="flex items-center gap-2 mt-3">
                  <img src={avatar} alt={author} className="w-7 h-7 rounded-full object-cover bg-gray-200 dark:bg-gray-700 ring-1 ring-gray-200 dark:ring-gray-700" />
                  <span className="text-xs text-gray-700 dark:text-gray-300 font-semibold">{author}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ════════════════════════════════════════════
            BOTTOM CTA
        ════════════════════════════════════════════ */}
        <div className="px-6 sm:px-12 py-10 bg-gradient-to-br from-blue-500/[0.04] via-cyan-500/[0.03] to-purple-500/[0.04] dark:from-blue-500/[0.06] dark:via-cyan-500/[0.04] dark:to-purple-500/[0.06] border-t border-gray-100 dark:border-gray-800/60">
          <div className="text-center">
            <h3 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-gray-100 mb-2">
              Ready to unlock cloud access?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Plans from $60/yr. Cancel anytime. Secured by Stripe.
            </p>
            <button
              onClick={onUpgrade}
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-bold text-base bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 text-white shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.03] transition-all duration-200 group"
            >
              {ctaText}
              <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
            </button>
            {dismissible && (
              <button
                onClick={handleDismiss}
                className="block mx-auto mt-3 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Maybe later
              </button>
            )}

            {/* Nominative-use disclaimer — Claude/Anthropic are referenced to
                describe compatibility, not partnership. */}
            <p className="text-[11px] leading-relaxed text-gray-400/80 dark:text-gray-500/80 max-w-md mx-auto mt-6">
              Claude is a trademark of Anthropic, PBC. Ultimate Web Scraper is an independent product and is not
              affiliated with, endorsed by, or sponsored by Anthropic.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpsellFloatingCard;
